import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Sun, Cloud, CloudSun, CloudRain, CloudLightning, CloudFog, Wind, Droplets, Search, Loader2 } from "lucide-react";

const iconFor = (desc = "") => {
  const d = desc.toLowerCase();
  if (d.includes("storm") || d.includes("thunder")) return CloudLightning;
  if (d.includes("rain") || d.includes("shower")) return CloudRain;
  if (d.includes("fog") || d.includes("mist") || d.includes("haze")) return CloudFog;
  if (d.includes("wind")) return Wind;
  if (d.includes("partly") || d.includes("mostly")) return CloudSun;
  if (d.includes("cloud")) return Cloud;
  if (d.includes("sunny") || d.includes("clear")) return Sun;
  return CloudSun;
};

const windyUrl = ({ lat, lon, zoom }) =>
  `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&detailLat=${lat}&detailLon=${lon}&zoom=${zoom}&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=km%2Fh&metricTemp=%C2%B0C&radarRange=-1`;

const DEFAULT = { name: "Gympie QLD", lat: -26.19, lon: 152.66, zoom: 5 };
const bneDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" }); } catch { return ""; } };

export default function WeatherWidget({ initialLocation, forecastDate }) {
  const [coords, setCoords] = useState(DEFAULT);
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadWeather = useCallback((q) => {
    setLoading(true);
    api.get("/weather", { params: { q } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = initialLocation?.trim();
    if (q) {
      loadWeather(q);
      api.get("/geocode", { params: { q } })
        .then((r) => { if (r.data?.found) setCoords({ name: r.data.display_name || q, lat: r.data.lat, lon: r.data.lon, zoom: 9 }); })
        .catch(() => {});
    } else {
      loadWeather(DEFAULT.name);
    }
  }, [initialLocation, loadWeather]);

  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    loadWeather(q);
    try {
      const r = await api.get("/geocode", { params: { q } });
      if (r.data?.found) setCoords({ name: r.data.display_name || q, lat: r.data.lat, lon: r.data.lon, zoom: 7 });
    } catch (err) { console.error("Weather geocode failed:", err); /* keep current map */ }
  };

  const obs = data?.observations || {};
  const daily = data?.daily || [];
  const jobDay = forecastDate ? bneDay(forecastDate) : null;
  const today = daily[0] || {};
  const cond = today.short_text || "";
  const Icon = iconFor(today.icon_descriptor || cond);
  const temp = obs.temp ?? today?.now?.temp_now ?? today.temp_max;
  const rain = today?.rain?.chance;
  const wind = obs?.wind?.speed_kilometre;
  const humidity = obs.humidity;
  const place = data?.location?.name || coords.name;

  const dayLabel = (iso, i) => {
    if (jobDay && bneDay(iso) === jobDay) return "Job day";
    if (i === 0) return "Today";
    try { return new Date(iso).toLocaleDateString("en-AU", { weekday: "short", timeZone: "Australia/Brisbane" }); } catch { return ""; }
  };

  return (
    <div className="bg-slate-900 text-white rounded-xl p-4 flex flex-col h-full" data-testid="weather-widget">
      <form onSubmit={search} className="relative mb-3" data-testid="weather-search-form">
        <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Change location…"
          className="pl-8 h-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="weather-search-input" />
      </form>

      {/* Multi-day forecast (job day highlighted) */}
      {daily.length > 0 && (
        <div className="mb-3" data-testid="weather-forecast">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
            {jobDay ? "Forecast — job day highlighted" : "Forecast"}
          </p>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {daily.slice(0, 7).map((d, i) => {
              const DIcon = iconFor(d.icon_descriptor || d.short_text || "");
              const isJobDay = jobDay && bneDay(d.date) === jobDay;
              return (
                <div key={d.date || i} data-testid={`forecast-day-${i}`}
                  className={`flex flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 min-w-[52px] shrink-0 border ${isJobDay ? "bg-lime-400/15 border-lime-400" : "bg-slate-800 border-slate-700"}`}>
                  <span className={`text-[10px] font-semibold ${isJobDay ? "text-lime-400" : "text-slate-300"}`}>{dayLabel(d.date, i)}</span>
                  <DIcon className={`h-4 w-4 ${isJobDay ? "text-lime-400" : "text-slate-300"}`} strokeWidth={1.5} />
                  <span className="text-xs font-bold">{d.temp_max != null ? `${Math.round(d.temp_max)}°` : "—"}</span>
                  <span className="text-[9px] text-sky-300 flex items-center gap-0.5"><CloudRain className="h-2.5 w-2.5" />{d.rain?.chance != null ? `${d.rain.chance}%` : "0%"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Current conditions */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="weather-current">
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin text-lime-400" />
        ) : (
          <>
            <Icon className="h-9 w-9 text-lime-400 shrink-0" strokeWidth={1.5} />
            <div className="leading-none">
              <span className="text-3xl font-bold font-heading">{temp != null ? `${Math.round(temp)}°` : "—"}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-300 truncate max-w-[140px]">{place}</p>
              {cond && <p className="text-xs text-slate-400 capitalize truncate max-w-[140px]">{cond}</p>}
            </div>
            <div className="ml-auto flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1" title="Rain chance"><CloudRain className="h-3.5 w-3.5 text-lime-400" />{rain != null ? `${rain}%` : "—"}</span>
              <span className="flex items-center gap-1" title="Wind"><Wind className="h-3.5 w-3.5 text-lime-400" />{wind != null ? `${wind}` : "—"}</span>
              <span className="flex items-center gap-1" title="Humidity"><Droplets className="h-3.5 w-3.5 text-lime-400" />{humidity != null ? `${humidity}%` : "—"}</span>
            </div>
          </>
        )}
      </div>

      {/* Windy map — fixed height so it renders fully */}
      <div className="mt-3 h-[300px] rounded-lg overflow-hidden border border-slate-700">
        <iframe
          title="Windy wind map"
          data-testid="windy-embed"
          key={`${coords.lat}-${coords.lon}-${coords.zoom}`}
          src={windyUrl(coords)}
          className="w-full h-full"
          frameBorder="0"
        />
      </div>
    </div>
  );
}
